// Prize ladder rendered on the right side of the KBC stage. Highlights current question,
// dims completed ones with a green tick, glows safe milestones.
import React from 'react';
import { motion } from 'framer-motion';
import { LADDER, SAFE_INDICES, JACKPOT_INDEX, formatPoints } from './kbcEngine';
import { Check, Lock } from 'lucide-react';

export default function PrizeLadder({ currentIndex, lockedIndex, lostAt }) {
    // currentIndex = index of the question being answered (0..15)
    // lockedIndex   = index of the LAST question answered correctly (-1 if none yet)
    // lostAt        = index where the player lost (red glow) — null if game running

    return (
        <div className="rounded-2xl bg-gradient-to-b from-slate-900/80 via-slate-950/90 to-black/90 ring-1 ring-amber-400/20 p-2 sm:p-3 backdrop-blur-md shadow-2xl"
             data-testid="kbc-prize-ladder">
            <div className="text-center text-[10px] sm:text-xs font-display font-extrabold uppercase tracking-[0.25em] text-amber-300 mb-2 sm:mb-3">
                Prize Ladder
            </div>
            <div className="flex flex-col-reverse gap-[3px] sm:gap-1">
                {LADDER.map((amount, i) => {
                    const isCurrent = i === currentIndex && lostAt === null;
                    const isLocked = i <= lockedIndex;
                    const isSafe = SAFE_INDICES.includes(i);
                    const isJackpot = i === JACKPOT_INDEX;
                    const isLost = lostAt === i;
                    return (
                        <Row
                            key={i}
                            index={i}
                            amount={amount}
                            isCurrent={isCurrent}
                            isLocked={isLocked}
                            isSafe={isSafe}
                            isJackpot={isJackpot}
                            isLost={isLost}
                        />
                    );
                })}
            </div>
        </div>
    );
}

function Row({ index, amount, isCurrent, isLocked, isSafe, isJackpot, isLost }) {
    let bg = 'bg-slate-800/40 text-slate-400';
    let ring = '';
    if (isLocked) bg = 'bg-emerald-900/40 text-emerald-100';
    if (isSafe && !isLost) bg = isLocked ? 'bg-amber-500/40 text-amber-50' : 'bg-amber-500/20 text-amber-200';
    if (isCurrent) {
        bg = 'bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-300 text-slate-900';
        ring = 'ring-2 ring-amber-100 shadow-[0_0_22px_rgba(253,224,71,0.85)]';
    }
    if (isJackpot && !isLocked && !isCurrent) {
        bg = 'bg-gradient-to-r from-fuchsia-700/40 to-amber-600/40 text-amber-100';
    }
    if (isLost) {
        bg = 'bg-red-600/80 text-white';
        ring = 'ring-2 ring-red-300';
    }

    return (
        <motion.div
            initial={false}
            animate={isCurrent ? { scale: [1, 1.04, 1] } : { scale: 1 }}
            transition={isCurrent ? { repeat: Infinity, duration: 1.6 } : { duration: 0.3 }}
            className={`relative flex items-center justify-between px-2 sm:px-3 py-1 sm:py-1.5 rounded-md font-display font-bold text-[10px] sm:text-xs ${bg} ${ring}`}
            data-testid={`kbc-ladder-row-${index + 1}`}
        >
            <div className="flex items-center gap-1 sm:gap-1.5">
                <span className={`inline-flex items-center justify-center w-4 sm:w-5 h-4 sm:h-5 rounded-full text-[8px] sm:text-[9px] font-extrabold ${
                    isLocked ? 'bg-emerald-500 text-white' : isCurrent ? 'bg-slate-900 text-amber-300' : 'bg-slate-700 text-slate-300'
                }`}>
                    {isLocked && !isCurrent ? <Check className="w-2.5 h-2.5" /> : (index + 1)}
                </span>
                {isSafe && <Lock className="w-2.5 h-2.5 opacity-70" />}
            </div>
            <span className={isJackpot ? 'text-[11px] sm:text-sm font-extrabold tracking-wide' : ''}>
                {formatPoints(amount)} pts
            </span>
        </motion.div>
    );
}
